import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import type {
  RotationOverview,
  RotationPreview,
  TimelineResponse,
  RotationConfig,
  ApiResponse,
} from '../../types/biweeklyRotation';
import OverviewTab from './biweekly/OverviewTab';
import NextRunTab from './biweekly/NextRunTab';
import HistoryTab from './biweekly/HistoryTab';
import SettingsTab from './biweekly/SettingsTab';

type TabId = 'overview' | 'next-run' | 'history' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  },
  {
    id: 'next-run',
    label: 'Next Run',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
  },
  {
    id: 'history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const BiweeklyRotationDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [overview, setOverview] = useState<RotationOverview | null>(null);
  const [preview, setPreview] = useState<RotationPreview | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [settings, setSettings] = useState<RotationConfig | null>(null);

  // Loading states
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);
      const response = await api.get<ApiResponse<RotationOverview>>('/admin/biweekly-rotation/overview');
      if (response.data.success && response.data.data) {
        setOverview(response.data.data);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching overview:', err);
      setError(err?.response?.data?.message || 'Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  // Fetch preview data
  const fetchPreview = useCallback(async () => {
    try {
      setLoadingPreview(true);
      const response = await api.get<ApiResponse<RotationPreview>>('/admin/biweekly-rotation/preview');
      if (response.data.success && response.data.data) {
        setPreview(response.data.data);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      setError(err?.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  // Fetch timeline data
  const fetchTimeline = useCallback(async (append = false) => {
    try {
      setLoadingTimeline(true);
      const offset = append && timeline ? timeline.pagination.offset + timeline.pagination.limit : 0;
      const response = await api.get<ApiResponse<TimelineResponse>>(`/admin/biweekly-rotation/timeline?limit=10&offset=${offset}`);
      if (response.data.success && response.data.data) {
        if (append && timeline) {
          setTimeline({
            ...response.data.data,
            timeline: [...timeline.timeline, ...response.data.data.timeline],
          });
        } else {
          setTimeline(response.data.data);
        }
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching timeline:', err);
      setError(err?.response?.data?.message || 'Failed to load timeline');
    } finally {
      setLoadingTimeline(false);
    }
  }, [timeline]);

  // Fetch settings data
  const fetchSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      const response = await api.get<ApiResponse<RotationConfig>>('/admin/biweekly-rotation/settings');
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setError(err?.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  // Save settings
  const saveSettings = useCallback(async (updates: Partial<RotationConfig>) => {
    try {
      setSavingSettings(true);
      const response = await api.patch<ApiResponse<RotationConfig>>('/admin/biweekly-rotation/settings', updates);
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        setError(null);
        // Refresh overview to reflect changes
        fetchOverview();
      }
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err?.response?.data?.message || 'Failed to save settings');
      throw err;
    } finally {
      setSavingSettings(false);
    }
  }, [fetchOverview]);

  // Skip rotation
  const skipRotation = useCallback(async (reason: string) => {
    try {
      const response = await api.post<ApiResponse<any>>('/admin/biweekly-rotation/skip', { reason });
      if (response.data.success) {
        setError(null);
        // Refresh data
        fetchOverview();
        fetchPreview();
        fetchTimeline();
      }
    } catch (err: any) {
      console.error('Error skipping rotation:', err);
      setError(err?.response?.data?.message || 'Failed to skip rotation');
    }
  }, [fetchOverview, fetchPreview, fetchTimeline]);

  // Trigger rotation
  const triggerRotation = useCallback(async () => {
    try {
      const response = await api.post<ApiResponse<any>>('/admin/biweekly-rotation/trigger');
      if (response.data.success) {
        setError(null);
        // Refresh data after a short delay
        setTimeout(() => {
          fetchOverview();
          fetchPreview();
          fetchTimeline();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error triggering rotation:', err);
      setError(err?.response?.data?.message || 'Failed to trigger rotation');
    }
  }, [fetchOverview, fetchPreview, fetchTimeline]);

  // Load data when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        if (!overview) fetchOverview();
        break;
      case 'next-run':
        if (!preview) fetchPreview();
        break;
      case 'history':
        if (!timeline) fetchTimeline();
        break;
      case 'settings':
        if (!settings) fetchSettings();
        break;
    }
  }, [activeTab, overview, preview, timeline, settings, fetchOverview, fetchPreview, fetchTimeline, fetchSettings]);

  // Initial load
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Biweekly Rotation</h2>
          <p className="text-neutral-400">
            Automated portfolio rebalancing every 14 days
          </p>
        </div>
        <span className="text-sm text-neutral-500">
          Last updated {new Date().toLocaleTimeString()}
        </span>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-2xl border border-red-500/40 bg-red-900/30 p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-red-200 font-medium">{error}</p>
            <button
              type="button"
              className="text-sm text-red-200/80 hover:text-red-100"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-white/10">
        <nav className="flex gap-1 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-cyan-400 text-cyan-400'
                  : 'border-transparent text-neutral-400 hover:text-white hover:border-white/30'
              }`}
              type="button"
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <OverviewTab
            overview={overview}
            loading={loadingOverview}
            onRefresh={fetchOverview}
            onNavigateToPreview={() => setActiveTab('next-run')}
            onNavigateToHistory={() => setActiveTab('history')}
            onNavigateToSettings={() => setActiveTab('settings')}
          />
        )}

        {activeTab === 'next-run' && (
          <NextRunTab
            preview={preview}
            loading={loadingPreview}
            onRefresh={fetchPreview}
            onSkipRotation={skipRotation}
            onTriggerRotation={triggerRotation}
          />
        )}

        {activeTab === 'history' && (
          <HistoryTab
            timeline={timeline}
            loading={loadingTimeline}
            onRefresh={() => fetchTimeline(false)}
            onLoadMore={() => fetchTimeline(true)}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            loading={loadingSettings}
            saving={savingSettings}
            onSave={saveSettings}
            onRefresh={fetchSettings}
          />
        )}
      </div>
    </div>
  );
};

export default BiweeklyRotationDashboard;
